import { useData } from "../context/DataContext";
import { ArrowLeft, BookOpen, Users, GraduationCap, ClipboardList, Link2 } from "lucide-react";
import { Button, EmptyState } from "./ui";

const FachDetail = () => {
  const { data, selectedFachId, setActiveTab } = useData();

  const fach = data.faecher.find((f) => f.id === selectedFachId);

  if (!fach) {
    return (
      <div className="space-y-6">
        <Button
          onClick={() => setActiveTab("faecher")}
          variant="secondary"
          icon={<ArrowLeft className="w-4 h-4" />}
        >
          Zurück zu Fächer
        </Button>
        <EmptyState
          icon="❓"
          title="Fach nicht gefunden"
          description="Das ausgewählte Fach existiert nicht mehr."
        />
      </div>
    );
  }

  // Get all zuteilungen for this subject
  const relatedZuteilungen = data.zuteilungen.filter(
    (z) => z.fachId === fach.id
  );

  // Get unique teachers who teach this subject (via zuteilungen)
  const teacherIds = [...new Set(relatedZuteilungen.map((z) => z.lpId).filter(Boolean))];
  const relatedLehrpersonen = data.lehrpersonen.filter((lp) =>
    teacherIds.includes(lp.id) || lp.faecher.includes(fach.kuerzel)
  );

  // Get unique classes where this subject is taught
  const classIds = [...new Set(relatedZuteilungen.map((z) => z.klasseId))];
  const relatedKlassen = data.klassen.filter((k) => classIds.includes(k.id));

  // Get gekoppelte klassen for this subject
  const relatedGekoppelt = data.gekoppelt.filter(
    (gk) => gk.fachKuerzel === fach.kuerzel
  );

  // Calculate statistics
  const totalLektionen = relatedZuteilungen.reduce((sum, z) => sum + z.lektionen, 0);
  const zuteilungenWithoutTeacher = relatedZuteilungen.filter((z) => !z.lpId).length;

  // Render compact stufen configuration
  const renderStufenConfig = () => {
    const pflichtStufen: number[] = [];
    const freifachStufen: number[] = [];
    const hkStufen: number[] = [];

    ([1, 2, 3, 4, 5, 6] as const).forEach((stufe) => {
      if (fach.pflichtfach?.[stufe]) pflichtStufen.push(stufe);
      if (fach.freifach?.[stufe]) freifachStufen.push(stufe);
      if (fach.hk?.[stufe]) hkStufen.push(stufe);
    });

    const badges: React.ReactNode[] = [];

    if (pflichtStufen.length > 0) {
      const hkForPflicht = hkStufen.filter((s) => pflichtStufen.includes(s));
      badges.push(
        <span key="pflicht" className="inline-flex items-center px-2 py-1 rounded text-sm font-medium bg-blue-100 text-blue-800">
          Pflicht: {pflichtStufen.join(", ")}
          {hkForPflicht.length > 0 && (
            <span className="ml-1 text-purple-700">(HK: {hkForPflicht.join(", ")})</span>
          )}
        </span>
      );
    }

    if (freifachStufen.length > 0) {
      const hkForFreifach = hkStufen.filter((s) => freifachStufen.includes(s));
      badges.push(
        <span key="freifach" className="inline-flex items-center px-2 py-1 rounded text-sm font-medium bg-green-100 text-green-800">
          Freifach: {freifachStufen.join(", ")}
          {hkForFreifach.length > 0 && (
            <span className="ml-1 text-purple-700">(HK: {hkForFreifach.join(", ")})</span>
          )}
        </span>
      );
    }

    return badges.length > 0 ? badges : <span className="text-gray-400 text-sm">Keine Stufen konfiguriert</span>;
  };

  const getKlasseName = (klasseId: string) => {
    return data.klassen.find((k) => k.id === klasseId)?.name || "Unbekannt";
  };

  const getLehrpersonName = (lpId: string | null) => {
    if (!lpId) return <span className="text-amber-600 italic">Nicht zugewiesen</span>;
    return data.lehrpersonen.find((lp) => lp.id === lpId)?.name || "Unbekannt";
  };

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Button
        onClick={() => setActiveTab("faecher")}
        variant="secondary"
        icon={<ArrowLeft className="w-4 h-4" />}
      >
        Zurück zu Fächer
      </Button>

      {/* Subject Header Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary-100 rounded-xl">
              <BookOpen className="w-8 h-8 text-primary-600" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-bold text-gray-900">{fach.name}</h2>
                <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                  {fach.kuerzel}
                </span>
              </div>
              <div className="mt-2 flex flex-wrap gap-3">
                {renderStufenConfig()}
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <ClipboardList className="w-4 h-4" />
              <span className="text-sm">Zuteilungen</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{relatedZuteilungen.length}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <BookOpen className="w-4 h-4" />
              <span className="text-sm">Lektionen gesamt</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{totalLektionen}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <Users className="w-4 h-4" />
              <span className="text-sm">Lehrpersonen</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{relatedLehrpersonen.length}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <GraduationCap className="w-4 h-4" />
              <span className="text-sm">Klassen</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{relatedKlassen.length}</p>
          </div>
        </div>

        {zuteilungenWithoutTeacher > 0 && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800">
              <strong>{zuteilungenWithoutTeacher}</strong> Zuteilung(en) ohne Lehrperson zugewiesen
            </p>
          </div>
        )}
      </div>

      {/* Profile-specific configurations */}
      {fach.profileConfig && Object.keys(fach.profileConfig).length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Profil-spezifische Konfigurationen</h3>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {Object.entries(fach.profileConfig).map(([profile, config]) => (
              <div key={profile} className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">Profil: {profile}</h4>
                <div className="space-y-1 text-sm">
                  {([1, 2, 3, 4, 5, 6] as const).map((stufe) => {
                    const items: string[] = [];
                    if (config.pflichtfach?.[stufe]) items.push("P");
                    if (config.freifach?.[stufe]) items.push("F");
                    if (config.hk?.[stufe]) items.push("H");
                    if (items.length === 0) return null;
                    return (
                      <div key={stufe} className="flex items-center gap-2">
                        <span className="text-gray-600">Stufe {stufe}:</span>
                        <div className="flex gap-1">
                          {items.map((item) => (
                            <span
                              key={item}
                              className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                item === "P"
                                  ? "bg-blue-100 text-blue-800"
                                  : item === "F"
                                  ? "bg-green-100 text-green-800"
                                  : "bg-purple-100 text-purple-800"
                              }`}
                            >
                              {item === "P" ? "Pflicht" : item === "F" ? "Frei" : "HK"}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Zuteilungen Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">Stundenzuteilungen</h3>
          </div>
        </div>
        {relatedZuteilungen.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            Keine Zuteilungen für dieses Fach vorhanden.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Klasse
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Lehrperson
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                    Typ
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                    Lektionen
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                    HK
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Bemerkungen
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {relatedZuteilungen.map((zt) => {
                  const klasse = data.klassen.find((k) => k.id === zt.klasseId);
                  return (
                    <tr key={zt.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 whitespace-nowrap">
                        <div className="font-medium text-gray-900">
                          {getKlasseName(zt.klasseId)}
                        </div>
                        {klasse && (
                          <div className="text-xs text-gray-500">
                            Stufe {klasse.stufe} · {klasse.profil}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm">
                        {getLehrpersonName(zt.lpId)}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-center">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            zt.typ === "Standard"
                              ? "bg-gray-100 text-gray-800"
                              : zt.typ === "HK"
                              ? "bg-purple-100 text-purple-800"
                              : zt.typ === "Praktikum"
                              ? "bg-orange-100 text-orange-800"
                              : zt.typ === "SHT"
                              ? "bg-blue-100 text-blue-800"
                              : "bg-teal-100 text-teal-800"
                          }`}
                        >
                          {zt.typ}
                        </span>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-center text-sm font-medium">
                        {zt.lektionen}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-center">
                        {zt.hatHk ? (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                            Ja
                          </span>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-500 max-w-xs truncate">
                        {zt.bemerkungen || "-"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Teachers Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">Lehrpersonen mit diesem Fach</h3>
          </div>
        </div>
        {relatedLehrpersonen.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            Keine Lehrpersonen für dieses Fach gefunden.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Name
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                    Vertr. Lekt.
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Alle Fächer
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                    Zuteilungen (dieses Fach)
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                    Lektionen (dieses Fach)
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {relatedLehrpersonen.map((lp) => {
                  const lpZuteilungen = relatedZuteilungen.filter((z) => z.lpId === lp.id);
                  const lpLektionen = lpZuteilungen.reduce((sum, z) => sum + z.lektionen, 0);
                  return (
                    <tr key={lp.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 whitespace-nowrap font-medium text-gray-900">
                        {lp.name}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-center text-sm">
                        {lp.solllektionen}
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex flex-wrap gap-1">
                          {lp.faecher.map((f) => (
                            <span
                              key={f}
                              className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                                f === fach.kuerzel
                                  ? "bg-primary-100 text-primary-800"
                                  : "bg-gray-100 text-gray-700"
                              }`}
                            >
                              {f}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-center text-sm">
                        {lpZuteilungen.length}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-center text-sm font-medium">
                        {lpLektionen}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Classes Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">Klassen mit diesem Fach</h3>
          </div>
        </div>
        {relatedKlassen.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            Keine Klassen mit diesem Fach gefunden.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Klasse
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                    Stufe
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                    Profil
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                    Bestand
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                    Lektionen
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Lehrperson
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {relatedKlassen.map((kl) => {
                  const klZuteilungen = relatedZuteilungen.filter((z) => z.klasseId === kl.id);
                  const klLektionen = klZuteilungen.reduce((sum, z) => sum + z.lektionen, 0);
                  const klTeachers = klZuteilungen
                    .map((z) => z.lpId)
                    .filter(Boolean)
                    .map((id) => data.lehrpersonen.find((lp) => lp.id === id)?.name)
                    .filter(Boolean);
                  return (
                    <tr key={kl.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 whitespace-nowrap font-medium text-gray-900">
                        {kl.name}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-center text-sm">
                        {kl.stufe}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-center">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                          {kl.profil}
                        </span>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-center text-sm">
                        {kl.bestand}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-center text-sm font-medium">
                        {klLektionen}
                      </td>
                      <td className="px-4 py-2 text-sm">
                        {klTeachers.length > 0 ? (
                          klTeachers.join(", ")
                        ) : (
                          <span className="text-amber-600 italic">Nicht zugewiesen</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Gekoppelte Klassen Section */}
      {relatedGekoppelt.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-900">Gekoppelte Klassen</h3>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Klasse 1
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Klasse 2
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                    Schüler Total
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Bemerkungen
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {relatedGekoppelt.map((gk) => (
                  <tr key={gk.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 whitespace-nowrap font-medium text-gray-900">
                      {getKlasseName(gk.klasse1Id)}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap font-medium text-gray-900">
                      {getKlasseName(gk.klasse2Id)}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-center text-sm">
                      {gk.schuelerTotal}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-500">
                      {gk.bemerkungen || "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default FachDetail;
